import { API_PATH, BASE_PATH } from '../config';
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Chip,
    IconButton,
    Tabs,
    Tab,
    Autocomplete,
    Popover,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { getTripTitle } from '../utils/tripTitle';
import './ExpenseManager.css';

const ExpenseManager = ({ tripId = null, userId = null, reportId = null, reportTitle = null, user = null }) => {
    const [expenses, setExpenses] = useState([]);
    const [balance, setBalance] = useState(null);
    const [summary, setSummary] = useState(null);
    const [tripParticipants, setTripParticipants] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openMembersModal, setOpenMembersModal] = useState(false);
    const [openAddMember, setOpenAddMember] = useState(false);
    const [openEditMember, setOpenEditMember] = useState(false);
    const [newMemberName, setNewMemberName] = useState('');
    const [editingMember, setEditingMember] = useState(null);
    const [editingMemberName, setEditingMemberName] = useState('');
    const [editingExpense, setEditingExpense] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showLegend, setShowLegend] = useState(false);
    const legendAnchorRef = useRef(null);
    const pieContainerRef = useRef(null);
    const [pieWidth, setPieWidth] = useState(0);

    // Measure pie container whenever summary tab becomes active or on resize
    useEffect(() => {
        if (tabValue !== 1) return;
        const measure = () => {
            if (pieContainerRef.current) {
                const w = pieContainerRef.current.offsetWidth;
                if (w > 0) setPieWidth(w);
            }
        };
        // Delay one frame so the DOM has painted
        const raf = requestAnimationFrame(() => {
            measure();
            // Also observe for future resizes
            if (pieContainerRef.current && typeof ResizeObserver !== 'undefined') {
                const ro = new ResizeObserver(measure);
                ro.observe(pieContainerRef.current);
                return () => ro.disconnect();
            }
        });
        window.addEventListener('resize', measure);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', measure);
        };
    }, [tabValue]);
    const [loading, setLoading] = useState(false);
    const [exchangeRates, setExchangeRates] = useState(null);
    const [defaultCurrency, setDefaultCurrency] = useState(user?.currency || 'USD');
    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [availableTrips, setAvailableTrips] = useState([]);
    const [selectedTripId, setSelectedTripId] = useState(null);
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignDialogError, setAssignDialogError] = useState('');
    const isStandalone = !tripId || tripId === 0;
    // Build user_id query param to include in API calls so backend knows who the current user is
    const userParam = userId ? `&user_id=${userId}` : '';
    const reportParam = reportId ? `&report_id=${reportId}` : '';
    const [recentCurrencies, setRecentCurrencies] = useState(() => {
        const saved = localStorage.getItem(`${isStandalone ? 'standalone' : `trip_${tripId}`}_recent_currencies`);
        return saved ? JSON.parse(saved) : [];
    });
    const [formData, setFormData] = useState({
        description: '',
        amount: '',
        category: 'OTHER',
        paid_by: '',
        participants: [],
        notes: '',
        currency: '',
    });

    const currencies = [
        // Major Currencies
        { value: 'USD', label: 'USD - United States Dollar', country: 'United States', symbol: '$' },
        { value: 'EUR', label: 'EUR - Euro', country: 'Eurozone', symbol: '€' },
        { value: 'GBP', label: 'GBP - British Pound Sterling', country: 'United Kingdom', symbol: '£' },
        { value: 'JPY', label: 'JPY - Japanese Yen', country: 'Japan', symbol: '¥' },
        { value: 'CNY', label: 'CNY - Chinese Yuan', country: 'China', symbol: '¥' },
        { value: 'CHF', label: 'CHF - Swiss Franc', country: 'Switzerland', symbol: 'CHF' },
        { value: 'AUD', label: 'AUD - Australian Dollar', country: 'Australia', symbol: 'A$' },
        { value: 'CAD', label: 'CAD - Canadian Dollar', country: 'Canada', symbol: 'C$' },
        { value: 'INR', label: 'INR - Indian Rupee', country: 'India', symbol: '₹' },
        { value: 'SGD', label: 'SGD - Singapore Dollar', country: 'Singapore', symbol: 'S$' },
        { value: 'HKD', label: 'HKD - Hong Kong Dollar', country: 'Hong Kong', symbol: 'HK$' },
        { value: 'NZD', label: 'NZD - New Zealand Dollar', country: 'New Zealand', symbol: 'NZ$' },
        { value: 'SEK', label: 'SEK - Swedish Krona', country: 'Sweden', symbol: 'kr' },
        { value: 'NOK', label: 'NOK - Norwegian Krone', country: 'Norway', symbol: 'kr' },
        { value: 'KRW', label: 'KRW - South Korean Won', country: 'South Korea', symbol: '₩' },
        
        // Americas
        { value: 'MXN', label: 'MXN - Mexican Peso', country: 'Mexico', symbol: '$' },
        { value: 'BRL', label: 'BRL - Brazilian Real', country: 'Brazil', symbol: 'R$' },
        { value: 'ARS', label: 'ARS - Argentine Peso', country: 'Argentina', symbol: '$' },
        { value: 'CLP', label: 'CLP - Chilean Peso', country: 'Chile', symbol: '$' },
        { value: 'COP', label: 'COP - Colombian Peso', country: 'Colombia', symbol: '$' },
        { value: 'PEN', label: 'PEN - Peruvian Sol', country: 'Peru', symbol: 'S/' },
        { value: 'VES', label: 'VES - Venezuelan Bolívar', country: 'Venezuela', symbol: 'Bs.' },
        
        // Europe
        { value: 'DKK', label: 'DKK - Danish Krone', country: 'Denmark', symbol: 'kr' },
        { value: 'PLN', label: 'PLN - Polish Złoty', country: 'Poland', symbol: 'zł' },
        { value: 'CZK', label: 'CZK - Czech Koruna', country: 'Czech Republic', symbol: 'Kč' },
        { value: 'HUF', label: 'HUF - Hungarian Forint', country: 'Hungary', symbol: 'Ft' },
        { value: 'RON', label: 'RON - Romanian Leu', country: 'Romania', symbol: 'lei' },
        { value: 'RUB', label: 'RUB - Russian Ruble', country: 'Russia', symbol: '₽' },
        { value: 'TRY', label: 'TRY - Turkish Lira', country: 'Turkey', symbol: '₺' },
        { value: 'UAH', label: 'UAH - Ukrainian Hryvnia', country: 'Ukraine', symbol: '₴' },
        { value: 'ISK', label: 'ISK - Icelandic Króna', country: 'Iceland', symbol: 'kr' },
        
        // Asia
        { value: 'THB', label: 'THB - Thai Baht', country: 'Thailand', symbol: '฿' },
        { value: 'MYR', label: 'MYR - Malaysian Ringgit', country: 'Malaysia', symbol: 'RM' },
        { value: 'IDR', label: 'IDR - Indonesian Rupiah', country: 'Indonesia', symbol: 'Rp' },
        { value: 'PHP', label: 'PHP - Philippine Peso', country: 'Philippines', symbol: '₱' },
        { value: 'VND', label: 'VND - Vietnamese Dong', country: 'Vietnam', symbol: '₫' },
        { value: 'PKR', label: 'PKR - Pakistani Rupee', country: 'Pakistan', symbol: '₨' },
        { value: 'BDT', label: 'BDT - Bangladeshi Taka', country: 'Bangladesh', symbol: '৳' },
        { value: 'LKR', label: 'LKR - Sri Lankan Rupee', country: 'Sri Lanka', symbol: 'Rs' },
        { value: 'NPR', label: 'NPR - Nepalese Rupee', country: 'Nepal', symbol: '₨' },
        { value: 'MMK', label: 'MMK - Myanmar Kyat', country: 'Myanmar', symbol: 'K' },
        
        // Middle East
        { value: 'AED', label: 'AED - UAE Dirham', country: 'United Arab Emirates', symbol: 'د.إ' },
        { value: 'SAR', label: 'SAR - Saudi Riyal', country: 'Saudi Arabia', symbol: '﷼' },
        { value: 'QAR', label: 'QAR - Qatari Riyal', country: 'Qatar', symbol: '﷼' },
        { value: 'ILS', label: 'ILS - Israeli Shekel', country: 'Israel', symbol: '₪' },
        { value: 'JOD', label: 'JOD - Jordanian Dinar', country: 'Jordan', symbol: 'د.ا' },
        { value: 'KWD', label: 'KWD - Kuwaiti Dinar', country: 'Kuwait', symbol: 'د.ك' },
        { value: 'BHD', label: 'BHD - Bahraini Dinar', country: 'Bahrain', symbol: 'ب.د' },
        { value: 'OMR', label: 'OMR - Omani Rial', country: 'Oman', symbol: '﷼' },
        
        // Africa
        { value: 'ZAR', label: 'ZAR - South African Rand', country: 'South Africa', symbol: 'R' },
        { value: 'NGN', label: 'NGN - Nigerian Naira', country: 'Nigeria', symbol: '₦' },
        { value: 'EGP', label: 'EGP - Egyptian Pound', country: 'Egypt', symbol: '£' },
        { value: 'MAD', label: 'MAD - Moroccan Dirham', country: 'Morocco', symbol: 'د.م.' },
        { value: 'KES', label: 'KES - Kenyan Shilling', country: 'Kenya', symbol: 'KSh' },
        { value: 'GHS', label: 'GHS - Ghanaian Cedi', country: 'Ghana', symbol: '₵' },
        { value: 'TND', label: 'TND - Tunisian Dinar', country: 'Tunisia', symbol: 'د.ت' },
        { value: 'UGX', label: 'UGX - Ugandan Shilling', country: 'Uganda', symbol: 'USh' },
        
        // Oceania & Others
        { value: 'FJD', label: 'FJD - Fijian Dollar', country: 'Fiji', symbol: '$' },
        { value: 'PGK', label: 'PGK - Papua New Guinean Kina', country: 'Papua New Guinea', symbol: 'K' },
        
        // Cryptocurrencies
        { value: 'BTC', label: 'BTC - Bitcoin', country: 'Cryptocurrency', symbol: '₿' },
        { value: 'ETH', label: 'ETH - Ethereum', country: 'Cryptocurrency', symbol: 'Ξ' },
        { value: 'USDT', label: 'USDT - Tether', country: 'Cryptocurrency', symbol: '₮' },
    ];

    // Sort currencies to show recently used at top
    const sortedCurrencies = (() => {
        const recentSet = new Set(recentCurrencies);
        const recent = currencies.filter(c => recentSet.has(c.value));
        const rest = currencies.filter(c => !recentSet.has(c.value));
        return [...recent, ...rest];
    })();

    // Track currency usage
    const trackCurrencyUsage = (currencyValue) => {
        setRecentCurrencies(prev => {
            const updated = [currencyValue, ...prev.filter(c => c !== currencyValue)].slice(0, 3);
            const storageKey = isStandalone ? 'standalone_recent_currencies' : `trip_${tripId}_recent_currencies`;
            localStorage.setItem(storageKey, JSON.stringify(updated));
            return updated;
        });
    };

    const categories = [
        { value: 'FOOD', label: 'Food & Dining' },
        { value: 'ACCOMMODATION', label: 'Accommodation' },
        { value: 'TRANSPORTATION', label: 'Transportation' },
        { value: 'ACTIVITY', label: 'Activities & Entertainment' },
        { value: 'SHOPPING', label: 'Shopping' },
        { value: 'OTHER', label: 'Other' },
    ];

    useEffect(() => {
        fetchExpenses();
        fetchBalance();
        fetchSummary();
        fetchTripParticipants();
        fetchExchangeRates();
    }, [tripId]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchTripParticipants = async () => {
        try {
            const endpoint = isStandalone
                ? `${API_PATH}/standalone_members.php?action=list${userParam}${reportParam}`
                : `${API_PATH}/trip_participants.php?trip_id=${tripId}${userParam}`;

            const response = await fetch(endpoint);
            const data = await response.json();
            if (data.success) {
                setTripParticipants(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching participants:', error);
        }
    };

    const handleAddMember = async () => {
        if (!newMemberName.trim()) {
            alert('Please enter a member name');
            return;
        }

        try {
            const endpoint = isStandalone
                ? `${API_PATH}/standalone_members.php?action=create${userParam}${reportParam}`
                : `${API_PATH}/trip_members.php`;

            const payload = isStandalone
                ? { name: newMemberName.trim(), report_id: reportId || undefined }
                : { trip_id: tripId, name: newMemberName.trim() };

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();
            if (data.success) {
                setNewMemberName('');
                setOpenAddMember(false);
                fetchTripParticipants();
            } else {
                alert('Error: ' + (data.error || 'Could not add member'));
            }
        } catch (error) {
            console.error('Error adding member:', error);
            alert('Failed to add member: ' + error.message);
        }
    };

    const handleEditMember = (member) => {
        setEditingMember(member);
        setEditingMemberName(member.name);
        setOpenEditMember(true);
    };

    const handleSaveEditedMember = async () => {
        if (!editingMemberName.trim()) {
            alert('Please enter a member name');
            return;
        }

        try {
            const endpoint = isStandalone
                ? `${API_PATH}/standalone_members.php?action=update${userParam}${reportParam}`
                : `${API_PATH}/trip_members.php?action=update&user_id=${editingMember.user_id}`;

            const payload = isStandalone
                ? { user_id: editingMember.user_id, name: editingMemberName.trim(), report_id: reportId || undefined }
                : { name: editingMemberName.trim() };

            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();
            if (data.success) {
                setEditingMember(null);
                setEditingMemberName('');
                setOpenEditMember(false);
                fetchTripParticipants();
            } else {
                alert('Error: ' + (data.error || 'Could not update member'));
            }
        } catch (error) {
            console.error('Error updating member:', error);
            alert('Failed to update member: ' + error.message);
        }
    };

    const fetchExchangeRates = async () => {
        try {
            const response = await fetch(`${API_PATH}/exchange_rates.php`);
            const data = await response.json();
            if (data.result === 'success') {
                setExchangeRates(data.rates);
            }
        } catch (error) {
            console.error('Error fetching exchange rates:', error);
        }
    };

    const fetchExpenses = async () => {
        try {
            setLoading(true);
            const tripParam = isStandalone ? 0 : tripId;
            const response = await fetch(
                `${API_PATH}/expenses.php?action=list&trip_id=${tripParam}${userParam}${reportParam}`
            );
            const data = await response.json();
            if (data.success) {
                setExpenses(data.data);
            }
        } catch (error) {
            console.error('Error fetching expenses:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBalance = async () => {
        try {
            const tripParam = isStandalone ? 0 : tripId;
            const response = await fetch(
                `${API_PATH}/trip_balance.php?trip_id=${tripParam}${userParam}${reportParam}`
            );
            const data = await response.json();
            if (data.success) {
                setBalance(data.data);
            }
        } catch (error) {
            console.error('Error fetching balance:', error);
        }
    };

    const fetchSummary = async () => {
        try {
            const tripParam = isStandalone ? 0 : tripId;
            const response = await fetch(
                `${API_PATH}/expenses.php?action=summary&trip_id=${tripParam}${userParam}${reportParam}`
            );
            const data = await response.json();
            if (data.success) {
                setSummary(data.data);
            }
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };

    const handleOpenDialog = (expense = null) => {
        if (expense) {
            console.log('Editing expense:', expense);
            setEditingExpense(expense);
            // Extract user IDs from participants array for checkboxes
            const participantIds = expense.participants 
                ? expense.participants.map(p => String(p.user_id || p))
                : [];
            console.log('Participant IDs:', participantIds);
            const formValues = {
                description: expense.description,
                amount: expense.amount,
                category: expense.category,
                paid_by: String(expense.paid_by_user_id || expense.paid_by || ''),
                participants: participantIds,
                notes: expense.notes || '',
                currency: expense.currency || '',
            };
            console.log('Setting form data:', formValues);
            setFormData(formValues);
        } else {
            setEditingExpense(null);
            // For new expenses, default to just the current user
            setFormData({
                description: '',
                amount: '',
                category: 'OTHER',
                paid_by: '',
                participants: userId ? [String(userId)] : [],
                notes: '',
                currency: '',
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingExpense(null);
    };

    const handleSaveExpense = async () => {
        try {
            // Validate required fields
            if (!formData.description.trim()) {
                alert('Please enter a description');
                return;
            }
            if (!formData.amount || parseFloat(formData.amount) <= 0) {
                alert('Please enter a valid amount');
                return;
            }
            if (!formData.paid_by) {
                alert('Please select who paid');
                return;
            }
            if (!formData.currency) {
                alert('Please select a currency');
                return;
            }
            if (formData.participants.length === 0) {
                alert('Please select at least one person to split with');
                return;
            }

            // Calculate split amount for each participant
            const amount = parseFloat(formData.amount);
            const splitAmount = amount / formData.participants.length;

            const participantsData = formData.participants.map(userId => ({
                user_id: parseInt(userId),
                amount_owed: splitAmount,
            }));

            const payload = {
                description: formData.description,
                amount: amount,
                category: formData.category,
                paid_by: parseInt(formData.paid_by),
                participants: participantsData,
                notes: formData.notes,
                currency: formData.currency,
            };

            // Pass trip_id=0 for standalone mode
            const tripParam = isStandalone ? 0 : tripId;
            const url = editingExpense
                ? `${API_PATH}/expenses.php?action=update&expense_id=${editingExpense.id}${userParam}`
                : `${API_PATH}/expenses.php?action=create&trip_id=${tripParam}${userParam}${reportParam}`;

            const method = editingExpense ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();
            if (data.success) {
                trackCurrencyUsage(formData.currency);
                handleCloseDialog();
                fetchExpenses();
                fetchBalance();
                fetchSummary();
            } else {
                alert('Error: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error saving expense:', error);
            alert('Failed to save expense: ' + error.message);
        }
    };

    const handleDeleteExpense = async (expenseId) => {
        if (!window.confirm('Are you sure you want to delete this expense?')) {
            return;
        }

        try {
            const response = await fetch(
                `${API_PATH}/expenses.php?action=delete&expense_id=${expenseId}${userParam}`,
                { method: 'DELETE' }
            );

            const data = await response.json();
            if (data.success) {
                fetchExpenses();
                fetchBalance();
                fetchSummary();
            }
        } catch (error) {
            console.error('Error deleting expense:', error);
        }
    };

    const getCurrencySymbol = (currency) => {
        const currencyObj = currencies.find(c => c.value === currency);
        return currencyObj ? currencyObj.symbol : currency;
    };

    const getCategoryColor = (category) => {
        const colors = {
            FOOD: '#FF6B6B',
            ACCOMMODATION: '#4ECDC4',
            TRANSPORTATION: '#45B7D1',
            ACTIVITY: '#FFA07A',
            SHOPPING: '#98D8C8',
            OTHER: '#9B9B9B',
        };
        return colors[category] || colors.OTHER;
    };

    const getCategoryLabel = (cat) => {
        const found = categories.find(c => c.value === cat);
        return found ? found.label : cat;
    };

    const getCategoryEmoji = (cat) => {
        const emojis = { FOOD: '🍽️', ACCOMMODATION: '🏨', TRANSPORTATION: '✈️', ACTIVITY: '🎯', SHOPPING: '🛍️', OTHER: '📌' };
        return emojis[cat] || '💰';
    };

    const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const formatAmount = (amount) => {
        const num = parseFloat(amount);
        if (isNaN(num)) return '0.00';
        return num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const convertToDefaultCurrency = (amount, fromCurrency) => {
        const num = parseFloat(amount);
        if (!exchangeRates) return num;
        if (fromCurrency === defaultCurrency) return num;
        const fromRate = exchangeRates[fromCurrency];
        const toRate = exchangeRates[defaultCurrency];
        if (!fromRate || !toRate) return num;
        return (num / fromRate) * toRate;
    };

    const updateDefaultCurrency = async (newCurrency) => {
        setDefaultCurrency(newCurrency);
        if (!user) return;
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_PATH}/update_profile.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ currency: newCurrency }),
            });
        } catch (error) {
            console.error('Error updating default currency:', error);
        }
    };

    const handleOpenAssignDialog = async () => {
        try {
            // When a reportId is present use the report-level trips endpoint
            const tripsUrl = reportId
                ? `${API_PATH}/expense_reports.php?action=trips${userParam}`
                : `${API_PATH}/expenses.php?action=get_user_trips${userParam}`;
            const response = await fetch(tripsUrl);
            const data = await response.json();
            setAvailableTrips(data.trips || []);
            setSelectedTripId(null);
            setAssignDialogError('');
            setOpenAssignDialog(true);
        } catch (e) {
            console.error('Failed to fetch trips', e);
        }
    };

    const handleAssignToTrip = async () => {
        if (!selectedTripId) return;
        setAssignLoading(true);
        setAssignDialogError('');
        try {
            if (reportId) {
                // Assign the whole report to the trip
                const response = await fetch(
                    `${API_PATH}/expense_reports.php?action=assign_trip${userParam}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id: reportId, trip_id: selectedTripId, user_id: userId }),
                    }
                );
                const data = await response.json();
                if (data.success) {
                    setOpenAssignDialog(false);
                    // Reload the current page so the report refreshes with the new trip
                    window.location.reload();
                } else {
                    setAssignDialogError(data.error || 'Failed to assign report to trip');
                }
            } else {
                // Legacy: assign individual standalone expenses to trip
                const response = await fetch(
                    `${API_PATH}/expenses.php?action=assign_to_trip${userParam}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ trip_id: selectedTripId }),
                    }
                );
                const data = await response.json();
                if (data.success) {
                    setOpenAssignDialog(false);
                    window.location.href = `${BASE_PATH}/trip/${selectedTripId}/expenses`;
                } else {
                    setAssignDialogError(data.error || 'Failed to assign to trip');
                }
            }
        } catch (e) {
            setAssignDialogError('Network error');
            console.error('Failed to assign trip', e);
        } finally {
            setAssignLoading(false);
        }
    };

    return (
        <Box className="expense-manager">
            <Box className="expense-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">{reportTitle || (isStandalone ? 'My Expenses' : 'Trip Expenses')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {isStandalone && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleOpenAssignDialog}
                            size="small"
                        >
                            Assign to Trip
                        </Button>
                    )}
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                        <Select
                            value={defaultCurrency}
                            onChange={(e) => updateDefaultCurrency(e.target.value)}
                            displayEmpty
                            sx={{ fontSize: '0.82rem', height: 32 }}
                        >
                            {currencies.map((c) => (
                                <MenuItem key={c.value} value={c.value} sx={{ fontSize: '0.82rem' }}>
                                    {c.value} {c.symbol !== c.value ? c.symbol : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        onClick={() => setOpenMembersModal(true)}
                        size="small"
                    >
                        Members
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        className="add-expense-btn"
                        size="small"
                    >
                        Add Expense
                    </Button>
                </Box>
            </Box>

            <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)}>
                <Tab label="Expenses" />
                <Tab label="Summary" />
                <Tab label="Settlements" />
            </Tabs>

            {/* Expenses Tab */}
            {tabValue === 0 && (
                <Box className="expenses-tab">
                    {expenses.length === 0 ? (
                        <Typography color="textSecondary">
                            No expenses yet. Add one to get started!
                        </Typography>
                    ) : (
                        <Box className="expenses-list">
                            {expenses.map((expense) => (
                                <Card
                                    key={expense.id}
                                    className="expense-card"
                                    style={{ '--cat-color': getCategoryColor(expense.category) }}
                                >
                                    <CardContent>
                                        {/* Top row: category badge + date */}
                                        <Box className="expense-top-row">
                                            <Chip
                                                label={`${getCategoryEmoji(expense.category)} ${getCategoryLabel(expense.category)}`}
                                                size="small"
                                                style={{
                                                    backgroundColor: hexToRgba(getCategoryColor(expense.category), 0.15),
                                                    color: getCategoryColor(expense.category),
                                                    border: `1px solid ${hexToRgba(getCategoryColor(expense.category), 0.4)}`,
                                                }}
                                            />
                                            <Typography variant="caption" className="expense-date">
                                                {new Date(expense.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </Typography>
                                        </Box>
                                        {/* Main row: description + amount */}
                                        <Box className="expense-header-row">
                                            <Box className="expense-info">
                                                <Typography variant="h6">
                                                    {expense.description}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Paid by {expense.paid_by_name}
                                                </Typography>
                                            </Box>
                                            <Box className="expense-amount">
                                                <Typography
                                                    variant="h5"
                                                    style={{ color: getCategoryColor(expense.category) }}
                                                >
                                                    {getCurrencySymbol(expense.currency)}{formatAmount(expense.amount)}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {expense.currency}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {/* Participants */}
                                        {expense.participants && expense.participants.length > 0 && (
                                            <Box className="participants">
                                                <Typography variant="subtitle2">
                                                    Split among:
                                                </Typography>
                                                <Box className="participant-list">
                                                    {expense.participants.map(
                                                        (p) => (
                                                            <Chip
                                                                key={p.user_id}
                                                                label={`${p.name}: ${getCurrencySymbol(expense.currency)}${formatAmount(p.amount_owed)}`}
                                                                size="small"
                                                            />
                                                        )
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                        {/* Actions */}
                                        <Box className="expense-actions">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(expense)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteExpense(expense.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* Summary Tab */}
            {tabValue === 1 && (
                <Box className="summary-tab">
                    {!summary || (!summary.by_category && !summary.by_currency) ? (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" color="textSecondary" align="center">
                                    No expense data available yet. Add some expenses to see the summary.
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            {/* Pie Chart by Category */}
                            {summary.by_category && summary.by_category.length > 0 && (
                        <Card className="chart-card" sx={{ mb: 3 }}>
                            <CardContent sx={{ overflow: 'hidden', px: { xs: 1, sm: 2 } }}>
                                <Typography variant="h6" gutterBottom sx={{ px: { xs: 0, sm: 2 } }}>
                                    Expenses by Category
                                </Typography>
                                {(() => {
                                    const aggregated = {};
                                    summary.by_category.forEach(cat => {
                                        if (!aggregated[cat.category]) aggregated[cat.category] = 0;
                                        aggregated[cat.category] += parseFloat(cat.total);
                                    });
                                    const pieData = Object.entries(aggregated).map(([category, total]) => ({ name: category, value: total }));
                                    return (
                                        <>
                                            <div ref={pieContainerRef} style={{ width: '100%', minWidth: 0 }}>
                                                {pieWidth > 0 && (
                                                    <PieChart width={pieWidth} height={isMobile ? 220 : 260}>
                                                        <Pie
                                                            data={pieData}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={false}
                                                            label={false}
                                                            outerRadius={Math.min(pieWidth / 2 - 10, isMobile ? 85 : 100)}
                                                            fill="#8884d8"
                                                            dataKey="value"
                                                        >
                                                            {pieData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={getCategoryColor(entry.name)} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(value) => [`${getCurrencySymbol(defaultCurrency)}${formatAmount(value)} ${defaultCurrency}`, 'Total']} />
                                                    </PieChart>
                                                )}
                                            </div>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                                                <Button
                                                    ref={legendAnchorRef}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setShowLegend(v => !v)}
                                                    sx={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.3 }}
                                                >
                                                    Legend
                                                </Button>
                                            </Box>
                                            <Popover
                                                open={showLegend}
                                                anchorEl={legendAnchorRef.current}
                                                onClose={() => setShowLegend(false)}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                                PaperProps={{ sx: { p: 1.5, borderRadius: 2, boxShadow: 3, minWidth: 140 } }}
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Box key={`legend-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: index < pieData.length - 1 ? 1 : 0 }}>
                                                        <span style={{
                                                            flexShrink: 0,
                                                            display: 'inline-block',
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            backgroundColor: getCategoryColor(entry.name)
                                                        }} />
                                                        <span style={{ color: getCategoryColor(entry.name), fontSize: '0.8rem' }}>
                                                            {entry.name}
                                                        </span>
                                                    </Box>
                                                ))}
                                            </Popover>
                                        </>
                                    );
                                })()}
                            </CardContent>
                        </Card>
                    )}

                    {/* Total by Currency */}
                    {summary.by_currency && summary.by_currency.length > 0 && (
                        <div className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-p-4 tw-mb-4">
                            <p className="tw-text-sm tw-font-semibold tw-text-muted-fore tw-mb-3">Expenses by Currency</p>
                            <div className={`tw-grid tw-gap-2 ${isMobile ? 'tw-grid-cols-1' : 'tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-4'}`}>
                                {summary.by_currency.map((curr) => (
                                    <div
                                        key={curr.currency}
                                        className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-rounded-xl tw-bg-surface-3/60 tw-border tw-border-surface-border/50 tw-transition-all tw-duration-150 hover:tw-bg-surface-3"
                                    >
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <span className="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-lg tw-bg-accent/15 tw-flex tw-items-center tw-justify-center">
                                                <span className="tw-text-xs tw-font-bold tw-text-accent">{curr.currency.slice(0,2)}</span>
                                            </span>
                                            <div>
                                                <p className="tw-text-xs tw-font-semibold tw-text-muted-fore">{curr.currency}</p>
                                                <p className="tw-text-[10px] tw-text-muted">{curr.count} expense{curr.count !== 1 ? 's' : ''}</p>
                                            </div>
                                        </div>
                                        <span className="tw-text-sm tw-font-bold tw-text-muted-fore tw-tabular-nums">
                                            {getCurrencySymbol(curr.currency)}{formatAmount(curr.total)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {summary.by_category && summary.by_category.length > 0 && (
                        <Card className="category-summary">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    By Category
                                </Typography>
                                <TableContainer>
                                    <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Category</TableCell>
                                            <TableCell align="right">
                                                Total ({defaultCurrency})
                                            </TableCell>
                                            <TableCell align="right">
                                                Count
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(() => {
                                            const aggCat = {};
                                            summary.by_category.forEach(cat => {
                                                if (!aggCat[cat.category]) aggCat[cat.category] = { total: 0, count: 0 };
                                                aggCat[cat.category].total += convertToDefaultCurrency(cat.total, cat.currency);
                                                aggCat[cat.category].count += parseInt(cat.count, 10);
                                            });
                                            return Object.entries(aggCat).map(([category, data]) => (
                                                <TableRow key={category}>
                                                    <TableCell>
                                                        <Chip
                                                            label={category}
                                                            style={{
                                                                backgroundColor: getCategoryColor(category),
                                                                color: '#fff',
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {getCurrencySymbol(defaultCurrency)}{formatAmount(data.total)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {data.count}
                                                    </TableCell>
                                                </TableRow>
                                            ));
                                        })()}
                                    </TableBody>
                                </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}

                    {summary.by_payer && summary.by_payer.length > 0 && (
                        <Card className="payer-summary">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    By Payer
                                </Typography>
                                <TableContainer>
                                    <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Person</TableCell>
                                            <TableCell align="right">
                                                Total Paid ({defaultCurrency})
                                            </TableCell>
                                            <TableCell align="right">
                                                Count
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(() => {
                                            const aggPayer = {};
                                            summary.by_payer.forEach(payer => {
                                                const key = payer.paid_by_user_id ?? payer.name;
                                                if (!aggPayer[key]) aggPayer[key] = { name: payer.name, total: 0, count: 0 };
                                                aggPayer[key].total += convertToDefaultCurrency(payer.total, payer.currency);
                                                aggPayer[key].count += parseInt(payer.count, 10);
                                            });
                                            return Object.entries(aggPayer).map(([key, data]) => (
                                                <TableRow key={key}>
                                                    <TableCell>{data.name}</TableCell>
                                                    <TableCell align="right">
                                                        {getCurrencySymbol(defaultCurrency)}{formatAmount(data.total)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {data.count}
                                                    </TableCell>
                                                </TableRow>
                                            ));
                                        })()}
                                    </TableBody>
                                </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}
                        </>
                    )}
                </Box>
            )}

            {/* Settlements Tab */}
            {tabValue === 2 && balance && (
                <Box className="settlements-tab">
                    <Card className="balances-card">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Who Owes What
                            </Typography>
                            {balance.suggested_settlements.length === 0 ? (
                                <Typography color="textSecondary">
                                    Everyone is settled up!
                                </Typography>
                            ) : (
                                <Box className="settlements-list">
                                    {balance.suggested_settlements.map(
                                        (settlement, idx) => (
                                            <Card
                                                key={idx}
                                                className="settlement-card"
                                            >
                                                <CardContent>
                                                    <Box className="settlement-info">
                                                        <Typography
                                                            variant="body1"
                                                        >
                                                            <strong>
                                                                {settlement.from_name}
                                                            </strong>{' '}
                                                            owes{' '}
                                                            <strong>
                                                                {settlement.to_name}
                                                            </strong>
                                                        </Typography>
                                                        <Typography
                                                            variant="h6"
                                                            color="error"
                                                        >
                                                            {getCurrencySymbol(settlement.currency)}
                                                            {formatAmount(settlement.amount)}</Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {settlement.currency}
                                                            </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        )
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {balance.historical_settlements.length > 0 && (
                        <Card className="history-card">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Settlement History
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>From</TableCell>
                                            <TableCell>To</TableCell>
                                            <TableCell align="right">
                                                Amount
                                            </TableCell>
                                            <TableCell>Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {balance.historical_settlements.map(
                                            (settlement) => (
                                                <TableRow
                                                    key={settlement.id}
                                                >
                                                    <TableCell>
                                                        {settlement.from_name}
                                                    </TableCell>
                                                    <TableCell>
                                                        {settlement.to_name}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        $
                                                        {formatAmount(settlement.amount)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(
                                                            settlement.settled_at
                                                        ).toLocaleDateString()}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            )}

            {/* Add/Edit Expense Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingExpense ? 'Edit Expense' : 'Add New Expense'}
                </DialogTitle>
                <DialogContent sx={{ overflowY: 'visible' }}>
                    <Box className="dialog-content" sx={{ pt: 2 }}>
                        <TextField
                            label="Description"
                            fullWidth
                            value={formData.description}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    description: e.target.value,
                                })
                            }
                            margin="normal"
                        />
                        <TextField
                            label="Amount"
                            type="text"
                            fullWidth
                            placeholder="0.00"
                            inputProps={{ 
                                inputMode: 'numeric'
                            }}
                            value={formData.amount ? (parseFloat(formData.amount) || 0).toFixed(2) : ''}
                            onChange={(e) => {
                                const input = e.target.value.replace(/[^0-9]/g, '');
                                if (input === '') {
                                    setFormData({ ...formData, amount: '' });
                                } else {
                                    // Convert cents to dollars (e.g., "123" -> 1.23)
                                    const cents = parseInt(input, 10);
                                    const dollars = (cents / 100).toFixed(2);
                                    setFormData({ ...formData, amount: dollars });
                                }
                            }}
                            margin="normal"
                        />
                        <Autocomplete
                            disablePortal
                            options={sortedCurrencies}
                            value={sortedCurrencies.find(c => c.value === formData.currency) || null}
                            onChange={(event, newValue) => {
                                setFormData({
                                    ...formData,
                                    currency: newValue ? newValue.value : '',
                                });
                            }}
                            getOptionLabel={(option) => {
                                const isRecent = recentCurrencies.includes(option.value);
                                return `${isRecent ? '★ ' : ''}${option.value} - ${option.country}`;
                            }}
                            renderOption={(props, option) => {
                                const isRecent = recentCurrencies.includes(option.value);
                                return (
                                    <Box 
                                        component="li" 
                                        {...props} 
                                        key={option.value}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 0.5,
                                            width: '100%',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {isRecent && <span style={{ marginRight: '4px', flexShrink: 0 }}>★</span>}
                                        <strong style={{ flexShrink: 0 }}>{option.value}</strong>
                                        <span style={{ flexShrink: 0 }}>{option.symbol}</span>
                                        <span style={{ flexShrink: 0 }}>-</span>
                                        <span style={{ 
                                            color: '#666', 
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {option.country}
                                        </span>
                                    </Box>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Currency *"
                                    placeholder="Search by code or country..."
                                    margin="normal"
                                    required
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            filterOptions={(options, { inputValue }) => {
                                const searchTerm = inputValue.toLowerCase();
                                return options.filter(option =>
                                    option.value.toLowerCase().includes(searchTerm) ||
                                    option.country.toLowerCase().includes(searchTerm) ||
                                    option.label.toLowerCase().includes(searchTerm)
                                );
                            }}
                            ListboxProps={{
                                style: {
                                    maxHeight: '300px'
                                }
                            }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        width: 'auto',
                                        minWidth: '280px',
                                        maxWidth: { xs: 'calc(100vw - 64px)', sm: '500px' }
                                    }
                                }
                            }}
                            sx={{ width: '100%', mt: 2 }}
                        />
                        
                        {/* Paid By Selection */}
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Who Paid?</InputLabel>
                            <Select
                                value={formData.paid_by}
                                label="Who Paid?"
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        paid_by: e.target.value,
                                    })
                                }
                            >
                                {tripParticipants.map((participant) => (
                                    <MenuItem key={participant.user_id} value={String(participant.user_id)}>
                                        {participant.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth margin="normal">
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.category}
                                label="Category"
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        category: e.target.value,
                                    })
                                }
                            >
                                {categories.map((cat) => (
                                    <MenuItem key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Split Between Selection */}
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Split Between:
                            </Typography>
                            <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 1, maxHeight: 200, overflow: 'auto' }}>
                                {tripParticipants.map((participant) => (
                                    <Box key={participant.user_id} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.participants.includes(String(participant.user_id))}
                                            onChange={(e) => {
                                                const userId = String(participant.user_id);
                                                if (e.target.checked) {
                                                    setFormData({
                                                        ...formData,
                                                        participants: [...formData.participants, userId],
                                                    });
                                                } else {
                                                    setFormData({
                                                        ...formData,
                                                        participants: formData.participants.filter(id => id !== userId),
                                                    });
                                                }
                                            }}
                                            style={{ marginRight: '8px', cursor: 'pointer' }}
                                        />
                                        <label style={{ cursor: 'pointer', flex: 1 }}>
                                            {participant.name}
                                        </label>
                                    </Box>
                                ))}
                            </Box>
                            {formData.participants.length > 0 && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Split: {formData.currency ? getCurrencySymbol(formData.currency) : ''}{formData.amount ? formatAmount(parseFloat(formData.amount) / formData.participants.length) : '0.00'} per person
                                </Typography>
                            )}
                        </Box>

                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            rows={2}
                            value={formData.notes}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    notes: e.target.value,
                                })
                            }
                            margin="normal"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSaveExpense}
                        variant="contained"
                        color="primary"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Trip Member Dialog */}
            <Dialog open={openAddMember} onClose={() => setOpenAddMember(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Add Trip Member</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Member Name"
                        fullWidth
                        value={newMemberName}
                        onChange={(e) => setNewMemberName(e.target.value)}
                        placeholder="Enter member name"
                        margin="normal"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleAddMember();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddMember(false)}>Cancel</Button>
                    <Button
                        onClick={handleAddMember}
                        variant="contained"
                        color="primary"
                    >
                        Add Member
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Trip Member Dialog */}
            <Dialog open={openEditMember} onClose={() => setOpenEditMember(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit Member Name</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Member Name"
                        fullWidth
                        value={editingMemberName}
                        onChange={(e) => setEditingMemberName(e.target.value)}
                        placeholder="Enter member name"
                        margin="normal"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSaveEditedMember();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditMember(false)}>Cancel</Button>
                    <Button
                        onClick={handleSaveEditedMember}
                        variant="contained"
                        color="primary"
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Members Modal */}
            <Dialog open={openMembersModal} onClose={() => setOpenMembersModal(false)} maxWidth="sm" fullWidth
                PaperProps={{
                    style: {
                        background: 'var(--color-surface-2, #1a1f2e)',
                        border: '1px solid var(--color-surface-border, rgba(255,255,255,0.08))',
                        borderRadius: '1rem',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                    }
                }}
            >
                <div className="tw-px-5 tw-pt-5 tw-pb-1 tw-flex tw-items-center tw-justify-between">
                    <h2 className="tw-text-base tw-font-bold tw-text-muted-fore">Trip Members</h2>
                    <button
                        onClick={() => setOpenMembersModal(false)}
                        className="tw-p-1.5 tw-rounded-xl tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all"
                        aria-label="Close"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>
                <DialogContent sx={{ px: 0, pb: 0, pt: 1 }}>
                    <div className="tw-px-5 tw-py-2">
                        {tripParticipants.length === 0 ? (
                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-10 tw-text-center">
                                <span className="tw-p-3 tw-rounded-full tw-bg-accent/5">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-text-muted"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                </span>
                                <p className="tw-text-xs tw-text-muted">No members yet. Add one to get started!</p>
                            </div>
                        ) : (
                            <div className="tw-flex tw-flex-col tw-gap-2">
                                {tripParticipants.map((member) => (
                                    <div
                                        key={member.user_id}
                                        className="tw-group tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2.5 tw-rounded-xl tw-bg-surface-3/50 tw-border tw-border-surface-border/40 hover:tw-bg-surface-3 tw-transition-all tw-duration-150"
                                    >
                                        <div className="tw-flex tw-items-center tw-gap-3">
                                            <span className="tw-flex-shrink-0 tw-w-9 tw-h-9 tw-rounded-xl tw-bg-accent/20 tw-flex tw-items-center tw-justify-center">
                                                <span className="tw-text-sm tw-font-bold tw-text-accent">
                                                    {(member.name || '?').charAt(0).toUpperCase()}
                                                </span>
                                            </span>
                                            <div>
                                                <p className="tw-text-sm tw-font-semibold tw-text-muted-fore">{member.name}</p>
                                                <span className={`tw-text-[10px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-md ${
                                                    member.access_level === 'owner'
                                                        ? 'tw-bg-amber-500/15 tw-text-amber-400'
                                                        : 'tw-bg-surface-3 tw-text-muted'
                                                }`}>
                                                    {member.access_level}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => { handleEditMember(member); setOpenMembersModal(false); }}
                                            aria-label={`Edit ${member.name}`}
                                            className="tw-p-1.5 tw-rounded-lg tw-text-muted hover:tw-text-accent-glow hover:tw-bg-accent/10 tw-opacity-0 tw-group-hover:tw-opacity-100 tw-transition-all tw-duration-150"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/></svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-5 tw-py-4 tw-border-t tw-border-surface-border/40 tw-mt-3">
                    <button
                        onClick={() => setOpenMembersModal(false)}
                        className="tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-font-medium tw-text-muted hover:tw-text-muted-fore hover:tw-bg-surface-3 tw-transition-all tw-duration-150"
                    >
                        Close
                    </button>
                    <button
                        onClick={() => { setOpenMembersModal(false); setOpenAddMember(true); }}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-accent hover:tw-bg-accent/90 tw-text-white tw-transition-all tw-duration-150"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
                        Add Member
                    </button>
                </div>
            </Dialog>

            {/* Assign to Trip Dialog */}
            <Dialog open={openAssignDialog} onClose={() => { setOpenAssignDialog(false); setAssignDialogError(''); }} maxWidth="sm" fullWidth>
                <DialogTitle>{reportId ? 'Assign Report to a Trip' : 'Assign Expenses to a Trip'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {reportId
                            ? 'Select a trip to link this expense report to. Only trips without an existing expense report are available.'
                            : 'All your standalone expenses will be moved to the selected trip and visible to all trip members.'}
                    </Typography>
                    {assignDialogError && (
                        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                            {assignDialogError}
                        </Typography>
                    )}
                    {availableTrips.length === 0 ? (
                        <Typography color="textSecondary">No active trips found. Create a trip first.</Typography>
                    ) : (
                        availableTrips.map(trip => (
                            <Box
                                key={trip.trip_id}
                                onClick={() => setSelectedTripId(trip.trip_id)}
                                sx={{
                                    p: 2, mb: 1,
                                    border: '1px solid',
                                    borderColor: selectedTripId === trip.trip_id ? 'primary.main' : 'divider',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    bgcolor: selectedTripId === trip.trip_id ? 'primary.light' : 'transparent',
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight={selectedTripId === trip.trip_id ? 600 : 400}>
                                    {getTripTitle(trip)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {trip.start_location}{trip.start_date ? ` · ${trip.start_date}` : ''}
                                </Typography>
                            </Box>
                        ))
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!selectedTripId || assignLoading}
                        onClick={handleAssignToTrip}
                    >
                        {assignLoading ? 'Assigning...' : 'Assign'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExpenseManager;
